import ChevronRight from '@mui/icons-material/ChevronRight';
import Info from '@mui/icons-material/Info';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import { alpha, lighten, styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import CellRow from '../Canvas/CellRow';

const PREFIX = 'ListItem';

const classes = {
  icon: `${PREFIX}-icon`,
  linkIcon: `${PREFIX}-linkIcon`,
  descriptionText: `${PREFIX}-descriptionText`,
  titleText: `${PREFIX}-titleText`,
  card: `${PREFIX}-card`,
  iconDisabled: `${PREFIX}-iconDisabled`,
  buttonHover: `${PREFIX}-buttonHover`,
  titleTextDisabled: `${PREFIX}-titleTextDisabled`,
  descriptionTextDisabled: `${PREFIX}-descriptionTextDisabled`,
  divider: `${PREFIX}-divider`,
  hover: `${PREFIX}-hover`,
  noHoverColor: `${PREFIX}-noHoverColor`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.icon}`]: {
    verticalAlign: 'middle',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    visibility: 'hidden'
  },

  [`& .${classes.linkIcon}`]: {
    verticalAlign: 'middle',
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2)
  },

  [`& .${classes.descriptionText}`]: {
    paddingLeft: theme.spacing(2),
    color: alpha(theme.palette.grey[900], 0.62)
  },

  [`& .${classes.titleText}`]: {
    paddingLeft: theme.spacing(2),
    color: alpha(theme.palette.grey[900], 0.9)
  },

  [`& .${classes.card}`]: {
    paddingLeft: theme.spacing(0),
    '&:hover': {
      background: 'none!important'
    }
  },

  [`& .${classes.iconDisabled}`]: {
    verticalAlign: 'middle',
    cursor: 'pointer',
    color: alpha(theme.palette.primary.main, 0.6),
    visibility: 'hidden'
  },

  [`& .${classes.buttonHover}`]: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.04)
    }
  },

  [`& .${classes.titleTextDisabled}`]: {
    paddingLeft: theme.spacing(2),
    color: alpha(theme.palette.grey[900], 0.62)
  },

  [`& .${classes.descriptionTextDisabled}`]: {
    paddingLeft: theme.spacing(2),
    color: alpha(theme.palette.grey[900], 0.62)
  },

  [`& .${classes.divider}`]: {
    marginLeft: theme.spacing(0),
    border: `0.5px solid ${lighten(theme.palette.primary.main, 0.6)}`,
    backgroundColor: lighten(theme.palette.primary.main, 0.6)
  },

  [`& .${classes.hover}`]: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.11),
      '& .ListItem-icon': {
        visibility: 'visible'
      },
      '& .ListItem-iconDisabled': {
        visibility: 'visible'
      }
    }
  },

  [`& .${classes.noHoverColor}`]: {
    '&:hover': {
      '& .ListItem-icon': {
        visibility: 'visible'
      },
      '& .ListItem-iconDisabled': {
        visibility: 'visible'
      }
    }
  }
}));

interface ListItemProps {
  title: string;
  description: string;
  enabled: boolean;
  iconOnClick: (e: any) => void;
  clickable?: boolean;
}

const ListItem = ({
  title,
  description,
  iconOnClick,
  clickable = true,
  enabled = true
}: ListItemProps) => {
  const getTitle = () => (
    <Typography
      className={enabled ? classes.titleText : classes.titleTextDisabled}
      variant="subtitle1"
      data-testid="listitem-title"
    >
      {title}
    </Typography>
  );

  const getDescription = () => (
    <Typography
      className={enabled ? classes.descriptionText : classes.descriptionTextDisabled}
      variant="body2"
      data-testid="listitem-description"
    >
      {description}
    </Typography>
  );

  return (
    <Root>
      <div className={clickable ? classes.hover : classes.noHoverColor}>
        <CellRow className={classes.card}>
          <ListItemText disableTypography primary={getTitle()} secondary={getDescription()} />
          {enabled ? (
            <IconButton
              data-testid="info-icon"
              onClick={iconOnClick}
              aria-label="properties"
              classes={{ root: classes.buttonHover }}
              size="large"
            >
              <Tooltip title="Properties">
                <Info className={enabled ? classes.icon : classes.iconDisabled} />
              </Tooltip>
            </IconButton>
          ) : null}
          {clickable && enabled ? <ChevronRight className={classes.linkIcon} /> : null}
        </CellRow>
      </div>
      <Divider variant="inset" component="li" classes={{ inset: classes.divider }} />
    </Root>
  );
};

export default ListItem;
